import type { AnyID, Item } from '@types'
import { Quaternion, Vector3 } from 'three'
import {
  computePouffArmApproachMetrics,
  computePouffSnapPositionToArm,
  isArmAnchorForPouff,
  isPouffApproachBlockedByArmOccupants,
  shouldPouffIgnoreArmObstaclesForDrag,
  shouldPouffSnapToArm,
} from './pouffArmSnap'

export const POUFF_SNAP_FACE_GAP_MM = 1

type AnchorAxes = {
  center: Vector3
  xAxis: Vector3
  zAxis: Vector3
  halfX: number
  halfZ: number
}

function readAnchorAxes(anchor: Item): AnchorAxes | null {
  if (!anchor.matrix) return null

  const pos = new Vector3()
  const quat = new Quaternion()
  anchor.matrix.decompose(pos, quat, new Vector3())

  return {
    center: new Vector3(anchor.width / 2, anchor.height / 2, anchor.thickness / 2)
      .applyQuaternion(quat)
      .add(pos),
    xAxis: new Vector3(1, 0, 0).applyQuaternion(quat),
    zAxis: new Vector3(0, 0, 1).applyQuaternion(quat),
    halfX: Number(anchor.width || 0) / 2,
    halfZ: Number(anchor.thickness || 0) / 2,
  }
}

function puffSupportOnAxis(
  puff: Item,
  puffQuat: Quaternion,
  axis: Vector3,
): number {
  const puffX = new Vector3(1, 0, 0).applyQuaternion(puffQuat)
  const puffY = new Vector3(0, 1, 0).applyQuaternion(puffQuat)
  const puffZ = new Vector3(0, 0, 1).applyQuaternion(puffQuat)
  return (
    (Number(puff.width || 0) / 2) * Math.abs(puffX.dot(axis)) +
    (Number(puff.height || 0) / 2) * Math.abs(puffY.dot(axis)) +
    (Number(puff.thickness || 0) / 2) * Math.abs(puffZ.dot(axis))
  )
}

/** Distancia del centro del puff al plano de cada cara del ancla (menor = cara más cercana). */
export function scorePouffSnapToAnchor(
  anchor: Item,
  puff: Item,
  draggedCornerPos: Vector3,
  items: Map<AnyID, Item>,
): number | null {
  if (isArmAnchorForPouff(anchor)) {
    if (!shouldPouffIgnoreArmObstaclesForDrag(anchor, puff, draggedCornerPos, items)) return null
    if (isPouffApproachBlockedByArmOccupants(anchor, puff, draggedCornerPos, items, puff.id)) {
      return null
    }
    const metrics = computePouffArmApproachMetrics(anchor, puff, draggedCornerPos, items)
    return metrics?.distToExteriorFace ?? null
  }

  const axes = readAnchorAxes(anchor)
  if (!axes || !puff.matrix) return null

  const puffQuat = new Quaternion()
  puff.matrix.decompose(new Vector3(), puffQuat, new Vector3())
  const puffCenter = new Vector3(puff.width / 2, puff.height / 2, puff.thickness / 2)
    .applyQuaternion(puffQuat)
    .add(draggedCornerPos)

  const delta = puffCenter.clone().sub(axes.center)
  delta.y = 0
  const xProj = axes.xAxis.dot(delta)
  const zProj = axes.zAxis.dot(delta)

  return Math.min(
    Math.abs(axes.halfX - xProj),
    Math.abs(axes.halfX + xProj),
    Math.abs(axes.halfZ - zProj),
    Math.abs(axes.halfZ + zProj),
  )
}

/**
 * Coloca la esquina del puff a tope en la cara del OBB del ancla más cercana
 * (izquierda, derecha, frente o atrás). Sin rotación.
 */
export function computePouffSnapPositionToAnchor(
  anchor: Item,
  puff: Item,
  draggedCornerPos: Vector3,
  items: Map<AnyID, Item>,
  gapMm = POUFF_SNAP_FACE_GAP_MM,
): Vector3 | null {
  if (!puff.matrix) return null

  if (isArmAnchorForPouff(anchor)) {
    if (!shouldPouffIgnoreArmObstaclesForDrag(anchor, puff, draggedCornerPos, items, puff.id)) {
      return null
    }
    if (isPouffApproachBlockedByArmOccupants(anchor, puff, draggedCornerPos, items, puff.id)) {
      return null
    }
    return computePouffSnapPositionToArm(anchor, puff, draggedCornerPos, items)
  }

  const axes = readAnchorAxes(anchor)
  if (!axes) return null

  const puffQuat = new Quaternion()
  puff.matrix.decompose(new Vector3(), puffQuat, new Vector3())
  const puffCenter = new Vector3(puff.width / 2, puff.height / 2, puff.thickness / 2)
    .applyQuaternion(puffQuat)
    .add(draggedCornerPos)

  const delta = puffCenter.clone().sub(axes.center)
  delta.y = 0
  const xProj = axes.xAxis.dot(delta)
  const zProj = axes.zAxis.dot(delta)

  type FaceCandidate = {
    onX: boolean
    sign: 1 | -1
    dist: number
  }

  const faces: FaceCandidate[] = [
    { onX: true, sign: 1, dist: Math.abs(axes.halfX - xProj) },
    { onX: true, sign: -1, dist: Math.abs(axes.halfX + xProj) },
    { onX: false, sign: 1, dist: Math.abs(axes.halfZ - zProj) },
    { onX: false, sign: -1, dist: Math.abs(axes.halfZ + zProj) },
  ]
  faces.sort((a, b) => a.dist - b.dist)
  const face = faces[0]

  const faceAxis = face.onX ? axes.xAxis : axes.zAxis
  const half = face.onX ? axes.halfX : axes.halfZ
  const currentProjection = face.onX ? xProj : zProj
  const faceNormal = faceAxis.clone().multiplyScalar(face.sign)
  const puffExtent = puffSupportOnAxis(puff, puffQuat, faceNormal)
  const desiredProjection = face.sign * (half + puffExtent + gapMm)
  const correction = desiredProjection - currentProjection

  const snappedCorner = draggedCornerPos.clone()
  snappedCorner.add(faceAxis.multiplyScalar(correction))
  snappedCorner.y = draggedCornerPos.y
  return snappedCorner
}

const POUFF_RELEASE_SNAP_MM = 60

function isValidPouffArmReleaseSnap(
  arm: Item,
  puff: Item,
  corner: Vector3,
  items: Map<AnyID, Item>,
  puffId: AnyID,
): number | null {
  if (!shouldPouffSnapToArm(arm, puff, corner, items)) return null
  if (isPouffApproachBlockedByArmOccupants(arm, puff, corner, items, puffId)) return null
  return scorePouffSnapToAnchor(arm, puff, corner, items)
}

/**
 * Elige ancla al soltar: prioriza el brazo al que se arrastró, luego el mejor brazo,
 * luego unidades — sin mezclar scores de brazos distintos con unidades lejanas.
 */
export function pickPouffSnapTargetOnRelease(
  puff: Item,
  corner: Vector3,
  puffId: AnyID,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  preferredArmId?: AnyID | null,
): AnyID | null {
  if (preferredArmId) {
    const preferred = items.get(preferredArmId)
    if (preferred && isArmAnchorForPouff(preferred)) {
      const score = isValidPouffArmReleaseSnap(preferred, puff, corner, items, puffId)
      if (score != null && score <= POUFF_RELEASE_SNAP_MM) {
        return preferredArmId
      }
    }
  }

  let bestArmId: AnyID | null = null
  let bestArmScore = Infinity

  for (const otherId of collisionableIds) {
    if (otherId === puffId) continue
    const other = items.get(otherId)
    if (!other || !isArmAnchorForPouff(other)) continue

    const score = isValidPouffArmReleaseSnap(other, puff, corner, items, puffId)
    if (score == null || score > POUFF_RELEASE_SNAP_MM) continue
    if (score < bestArmScore) {
      bestArmScore = score
      bestArmId = otherId
    }
  }

  let bestUnitId: AnyID | null = null
  let bestUnitScore = Infinity

  for (const otherId of collisionableIds) {
    if (otherId === puffId) continue
    const other = items.get(otherId)
    if (!other || isArmAnchorForPouff(other)) continue

    const score = scorePouffSnapToAnchor(other, puff, corner, items)
    if (score == null || score > POUFF_RELEASE_SNAP_MM) continue
    if (score < bestUnitScore) {
      bestUnitScore = score
      bestUnitId = otherId
    }
  }

  if (bestArmId && bestUnitId) {
    if (preferredArmId && String(bestArmId) === String(preferredArmId)) return bestArmId
    return bestArmScore <= bestUnitScore ? bestArmId : bestUnitId
  }

  return bestArmId ?? bestUnitId
}
